/**
 * Exit-only opposite side (futures, manual orders).
 *
 * While a position is open, an order on the OPPOSITE side is an exit and
 * nothing more: its size is capped at |position|, and it goes out as a plain
 * order (market, or the user's limit/stop) - never as a bracket, which would
 * attach fresh TP/SL legs that later open a position the other way. A
 * reversal is flatten, then a new entry.
 *
 * The same-side add and the flat entry are not touched by any of this.
 * tradovate-service refuses a manual opposite-side order over the position
 * with EXIT_EXCEEDS_POSITION (services/ExitOnlyGate.ts); this is the client
 * half, so the button already says what it will send.
 */

import { formatRestrainRemaining } from '../store/restrainStore';

export type OrderSide = 'Buy' | 'Sell';

export type ManualOrderPlan =
  /** No position: a normal entry, exactly as before. */
  | { kind: 'entry'; quantity: number }
  /** Same side as the position: an add, exactly as before. */
  | { kind: 'add'; quantity: number }
  /** Opposite side: an exit of `quantity` (= min(requested, size)). */
  | { kind: 'exit'; quantity: number; requested: number; positionSize: number; closes: boolean };

/** What a manual BUY / SELL of `quantity` does to a position of `netPos` (signed, 0/null = flat). */
export function planManualOrder(netPos: number | null | undefined, action: OrderSide, quantity: number): ManualOrderPlan {
  const net = Number(netPos);
  if (!Number.isFinite(net) || net === 0) return { kind: 'entry', quantity };
  const side = action === 'Buy' ? 1 : -1;
  if (Math.sign(net) === side) return { kind: 'add', quantity };
  const positionSize = Math.abs(net);
  const exitQty = Math.min(quantity, positionSize);
  return { kind: 'exit', quantity: exitQty, requested: quantity, positionSize, closes: exitQty === positionSize };
}

/**
 * Did tradovate-service already size the position's TP/SL for this exit?
 * A partial exit next to TP/SL legs has the legs modified down to what is
 * left before it is sent; the caller must then NOT re-place them (that would
 * cancel the broker's bracket and put two plain orders in its place).
 */
export function serverSizedLegs(result: unknown): boolean {
  return !!(result && typeof result === 'object' && (result as { protectiveLegs?: { handled?: unknown } }).protectiveLegs?.handled);
}

/** The restrain running on the position, as far as the button cares. */
export interface RestrainLabelInput {
  /** Epoch ms the restrain ends, or null for "until flat". */
  endsAt: number | null;
}

export interface ManualOrderButtonOptions {
  /** The active restrain on this position, or null/undefined when none. */
  restrain?: RestrainLabelInput | null;
  /** Clock for the restrain countdown (defaults to Date.now()). */
  now?: number;
  /** Narrow button: "RST" instead of "restrain". */
  compact?: boolean;
}

export interface ManualOrderButton {
  /** Button text: "BUY", "CLOSE 3", "EXIT 2 of 3 short", "LOCKED · restrain 1:52:10". */
  label: string;
  /** Tooltip saying what the click does (or why it can't). */
  title: string;
  /** Restrain is blocking this side (the button is greyed). */
  locked: boolean;
}

/** "1:52:10" over an hour, "4:12" under, "until flat" with no end. */
export function restrainCountdown(endsAt: number | null, now: number = Date.now()): string {
  if (endsAt == null) return 'until flat';
  return formatRestrainRemaining(endsAt - now);
}

/**
 * Label + tooltip for the BUY / SELL button at `quantity` against a position
 * of `netPos` (signed, 0/null = flat). Single source of truth for the desktop
 * action bar, the trading panel and the mobile bar.
 *
 * - flat: "BUY" / "SELL", as always.
 * - same side (add): "BUY" / "SELL"; "LOCKED · restrain ..." while a restrain runs.
 * - opposite side, closes: "CLOSE 3" - never locked (restrain lets you close).
 * - opposite side, partial: "EXIT 2 of 3 short"; "LOCKED · restrain ..." while a restrain runs.
 */
export function manualOrderButton(
  netPos: number | null | undefined,
  action: OrderSide,
  quantity: number,
  opts: ManualOrderButtonOptions = {},
): ManualOrderButton {
  const word = action === 'Buy' ? 'BUY' : 'SELL';
  const plan = planManualOrder(netPos, action, quantity);
  if (plan.kind === 'entry') return { label: word, title: `Place ${action} Order`, locked: false };

  const size = Math.abs(Number(netPos));
  const side = Number(netPos) > 0 ? 'long' : 'short';
  const changesSize = plan.kind === 'add' || !plan.closes;
  if (opts.restrain && changesSize) {
    const tag = opts.compact ? 'RST' : 'restrain';
    return {
      label: `LOCKED · ${tag} ${restrainCountdown(opts.restrain.endsAt, opts.now ?? Date.now())}`,
      title: `Restrain is running - size is frozen. Set size to ${size} to close, or use Flatten.`,
      locked: true,
    };
  }
  if (plan.kind === 'add') return { label: word, title: `Place ${action} Order`, locked: false };
  if (plan.closes) {
    return { label: `CLOSE ${plan.quantity}`, title: `${action} ${plan.quantity} to close your ${side} ${size}`, locked: false };
  }
  return {
    label: `EXIT ${plan.quantity} of ${size} ${side}`,
    title: `${action} ${plan.quantity} to reduce your ${side} ${size} to ${size - plan.quantity}`,
    locked: false,
  };
}

/** Just the label of manualOrderButton, without a restrain. */
export function manualOrderLabel(netPos: number | null | undefined, action: OrderSide, quantity: number): string {
  return manualOrderButton(netPos, action, quantity).label;
}

const EXCHANGE_SUFFIX = /\.(CME|CBOT|NYMEX|COMEX|NYBOT|KCBT|MGE)$/i;

interface PositionLike {
  symbol?: string;
  netPos: number;
  accountId?: number | string | null;
}

/**
 * The open position an order on `symbol` for `accountId` acts on. Same
 * symbol match the order panels have always used (exchange suffix stripped,
 * prefix either way; a position with no symbol counts), narrowed to the
 * account when both sides name one - the position is per broker + account +
 * contract.
 */
export function findOpenPosition<P extends PositionLike>(
  positions: readonly P[],
  symbol: string,
  accountId?: number | string | null,
): P | undefined {
  const want = symbol.replace(EXCHANGE_SUFFIX, '');
  return positions.find(pos => {
    if (pos.netPos === 0) return false;
    if (accountId != null && pos.accountId != null && String(pos.accountId) !== String(accountId)) return false;
    if (!pos.symbol) return true;
    const have = pos.symbol.replace(EXCHANGE_SUFFIX, '');
    return have === want || have.startsWith(want) || want.startsWith(have);
  });
}
